/**
 * Liquid Glass, drawn by the GPU (2026-10-24, Adam: "Build the highest quality version of liquid
 * glass on Safari 27 in the world … ALWAYS find a way to do refraction").
 *
 * Safari draws `backdrop-filter` with Core Animation's filters, which cannot bend what lies behind
 * (an SVG displacement in a backdrop filter is drawn by Chromium alone: WebKit bug 245510). But what
 * lies behind MapMark's glass is the plan, and the plan is ours. Every frame the editor draws it, the
 * plan round each glass control is copied — a CSS px a texel, sharp enough to be seen through — into
 * one atlas read once into memory (`Capture`). Each control has a WebGL canvas of its own under its
 * contents (`GlassCanvas`): it takes its region, frosts it as far as it is frosted (a dual-Kawase
 * pyramid: four steps down, up again to three strengths) and draws its glass with one shader — so the glass
 * stacks, moves, scales and fades with its control as any part of it does. (Measured in Safari 27:
 * a control drawn so costs hundredths of a millisecond; copying a shared GL canvas into each
 * control's canvas cost twelve, the GPU read back every time.)
 *
 * The glass itself: a slab whose edge is a rounded lip. Over the lip the world outside the control
 * is bent in towards its edge — more the nearer the edge, as a circle's profile — the colours parting
 * a little as a lens parts them; inside, the plan is frosted, its colour deepened and lifted, under the
 * glass's tint. It is lit from straight above: a fine bright line along its top edge and, fainter,
 * along its foot (the light through it), a soft glow inside each; its ends, where it is seen
 * edge-on through its thickness, a little darker, with a darker hairline. Nothing on it is off-centre.
 */

export interface GlassLook {
  /** The tint laid over what is seen through it (0–1 rgb), and how much of it over plain paper. */
  tint: [number, number, number, number];
  /** Colour deepened (1 = as it is) and lifted (1 = as it is). */
  sat: number;
  bright: number;
  /** How frosted, all over: -1 not at all (the plan as it is), 0 a breath (a few px), 1, 2 and 3 the pyramid's stronger frosts (in between, a mix). */
  blur: number;
  /** How far its rim bends the world just outside in, as a share of the rim's width; how steeply towards the edge (a power of the depth into the rim). */
  refract: number;
  curve: number;
  /** How far red, green and blue part at the very edge (a share of the bending). */
  disperse: number;
  /** How wide the bevel is, as a share of the control's height (4 to 10 px). */
  bezel: number;
  /** The light: the one crisp line round the edge — along the top, along the foot, round the ends. */
  rimTop: number;
  rimFoot: number;
  sideLine: number;
  /** What is behind is dimmed first (a sheet's backdrop): 1, not at all. */
  dim: number;
  /** How much more tint, at most, over plan that would make its words hard to read (busy, or the tint's opposite) — one amount for the whole control. */
  adapt: number;
}

/** Where a control's glass is drawn: its size (CSS px), its corner radius, and where it is on the screen. */
export interface PaneGeom {
  w: number;
  h: number;
  r: number;
  dpr: number;
  /** Its top-left on the screen (CSS px), and how much it is scaled there (an entrance under way). */
  x: number;
  y: number;
  k: number;
}

/** A part of the screen round a glass control, as copied: where it is (CSS px), its size in texels, and its place in the atlas. */
export interface Region {
  x: number;
  y: number;
  w: number;
  h: number;
  tw: number;
  th: number;
  ax: number;
  ay: number;
  /** Which copy it is (a control takes its region afresh only when this changes). */
  stamp: number;
}

/**
 * The plan round the glass controls, copied: the editor's layers over its backdrop, each control's
 * region into its own place in one atlas (rows of them), read into memory once — whatever the
 * number of controls, one copy and one reading a frame.
 */
export class Capture {
  readonly #atlas: HTMLCanvasElement;
  readonly #ax: CanvasRenderingContext2D;
  pixels: Uint8Array = new Uint8Array(0);
  /** The atlas's width (texels): a row of the pixels. */
  width = 0;
  #stamp = 0;
  constructor() {
    this.#atlas = document.createElement('canvas');
    this.#ax = this.#atlas.getContext('2d', { alpha: false })!;
  }
  /** The regions wanted (CSS px, and texels per CSS px), copied; each given its place. */
  take(layers: { canvas: HTMLCanvasElement; rect: DOMRect }[], backdrop: string, wants: { x: number; y: number; w: number; h: number; s: number }[]): Region[] {
    // rows of regions, tallest first, no wider than the widest region or 2048 texels
    const sized = wants.map((r, i) => ({ i, r, tw: Math.max(1, Math.ceil(r.w * r.s)), th: Math.max(1, Math.ceil(r.h * r.s)) }));
    const rowW = Math.max(2048, ...sized.map((q) => q.tw));
    const order = [...sized].sort((a, b) => b.th - a.th);
    const out: Region[] = new Array(wants.length);
    let x = 0,
      y = 0,
      rowH = 0,
      used = 0;
    const stamp = ++this.#stamp;
    for (const q of order) {
      if (x + q.tw > rowW) {
        y += rowH + 1;
        x = 0;
        rowH = 0;
      }
      out[q.i] = { x: q.r.x, y: q.r.y, w: q.r.w, h: q.r.h, tw: q.tw, th: q.th, ax: x, ay: y, stamp };
      x += q.tw + 1;
      rowH = Math.max(rowH, q.th);
      used = Math.max(used, x);
    }
    const H = Math.max(1, y + rowH);
    const W = Math.max(1, Math.min(rowW, used));
    if (this.#atlas.width < W || this.#atlas.height < H) {
      this.#atlas.width = Math.max(this.#atlas.width, Math.ceil(W / 64) * 64);
      this.#atlas.height = Math.max(this.#atlas.height, Math.ceil(H / 64) * 64);
    }
    const c = this.#ax;
    c.imageSmoothingEnabled = true;
    c.imageSmoothingQuality = 'high';
    for (const [i, q] of sized.entries()) {
      const g = out[i]!;
      c.save();
      c.setTransform(1, 0, 0, 1, 0, 0);
      c.beginPath();
      c.rect(g.ax, g.ay, g.tw, g.th);
      c.clip();
      c.fillStyle = backdrop;
      c.fillRect(g.ax, g.ay, g.tw, g.th);
      // the screen, so placed that the region falls in its place
      c.setTransform(q.r.s, 0, 0, q.r.s, g.ax - g.x * q.r.s, g.ay - g.y * q.r.s);
      for (const l of layers) if (l.canvas.width && l.canvas.height && l.rect.width && l.rect.height) c.drawImage(l.canvas, l.rect.left, l.rect.top, l.rect.width, l.rect.height);
      c.restore();
    }
    const d = c.getImageData(0, 0, this.#atlas.width, H).data;
    this.pixels = new Uint8Array(d.buffer, d.byteOffset, d.byteLength);
    this.width = this.#atlas.width;
    return out;
  }
}

const VERT = `#version 300 es
void main() {
  vec2 p = vec2(float((gl_VertexID << 1) & 2), float(gl_VertexID & 2));
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

/** Dual Kawase, down: the middle and four diagonal half-texels (Bjørge, "Bandwidth-efficient rendering", 2015). */
const DOWN = `#version 300 es
precision highp float;
uniform sampler2D u_src;
uniform vec2 u_half;
uniform vec2 u_out;
out vec4 o;
void main() {
  vec2 uv = gl_FragCoord.xy / u_out;
  vec4 s = texture(u_src, uv) * 4.0;
  s += texture(u_src, uv - u_half);
  s += texture(u_src, uv + u_half);
  s += texture(u_src, uv + vec2(u_half.x, -u_half.y));
  s += texture(u_src, uv - vec2(u_half.x, -u_half.y));
  o = s / 8.0;
}`;

/** Dual Kawase, up: a ring of eight round the point, the diagonals twice. */
const UP = `#version 300 es
precision highp float;
uniform sampler2D u_src;
uniform vec2 u_half;
uniform vec2 u_out;
out vec4 o;
void main() {
  vec2 uv = gl_FragCoord.xy / u_out;
  vec2 h = u_half;
  vec4 s = texture(u_src, uv + vec2(-h.x * 2.0, 0.0));
  s += texture(u_src, uv + vec2(-h.x, h.y)) * 2.0;
  s += texture(u_src, uv + vec2(0.0, h.y * 2.0));
  s += texture(u_src, uv + vec2(h.x, h.y)) * 2.0;
  s += texture(u_src, uv + vec2(h.x * 2.0, 0.0));
  s += texture(u_src, uv + vec2(h.x, -h.y)) * 2.0;
  s += texture(u_src, uv + vec2(0.0, -h.y * 2.0));
  s += texture(u_src, uv + vec2(-h.x, -h.y)) * 2.0;
  o = s / 12.0;
}`;

const GLASS = `#version 300 es
precision highp float;
uniform sampler2D u_sharp, u_b0, u_b1, u_b2, u_b3;
uniform vec2 u_regOrg;   // the region copied: its top-left on the screen (CSS px) …
uniform vec2 u_regCss;   // … and its size (CSS px)
uniform vec2 u_view;     // the screen's size (CSS px): past its edges the plan is mirrored back in
uniform float u_ph;      // the canvas's height (device px)
uniform float u_dpr;
uniform vec2 u_size;     // the control's size (CSS px)
uniform float u_r;       // its corner radius
uniform float u_bezel;   // the width of its lip
uniform vec2 u_org;      // its top-left on the screen (CSS px)
uniform float u_k;       // screen px per px of it (scaled as it comes in)
uniform vec4 u_tint;     // its tint, and how much of it (already as much as what lies under it needs: see glass.ts)
uniform float u_sat, u_bright, u_blur, u_refract, u_curve, u_disperse, u_dim;
uniform float u_rimTop, u_rimFoot, u_sideLine;
out vec4 o;

float sdRound(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}
// what lies behind a point of the screen, frosted to a level: -1 the plan as it is, 0 to 3 the pyramid's four frosts
vec3 behind(vec2 s, float level) {
  // (beyond the screen's edge there is nothing to bend in: what lies just inside it is, as a mirror would show it)
  vec2 c = abs(s);
  c = u_view - abs(u_view - c);
  vec2 uv = clamp((c - u_regOrg) / u_regCss, vec2(0.0), vec2(1.0));
  if (level < 0.0) return mix(texture(u_sharp, uv).rgb, texture(u_b0, uv).rgb, level + 1.0);
  if (level < 1.0) return mix(texture(u_b0, uv).rgb, texture(u_b1, uv).rgb, level);
  if (level < 2.0) return mix(texture(u_b1, uv).rgb, texture(u_b2, uv).rgb, level - 1.0);
  return mix(texture(u_b2, uv).rgb, texture(u_b3, uv).rgb, level - 2.0);
}
float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

void main() {
  vec2 px = vec2(gl_FragCoord.x, u_ph - gl_FragCoord.y) / u_dpr;
  vec2 half_ = u_size * 0.5;
  vec2 p = px - half_;
  float d = sdRound(p, half_, u_r);
  float aa = 0.6 / u_dpr;
  float cover = clamp(0.5 - d / (2.0 * aa), 0.0, 1.0);
  if (cover <= 0.0) { o = vec4(0.0); return; }

  // Its edge's outward normal, continuous all round (the round ends turning smoothly from the top to the side
  // to the foot), and how far into the bevel this point is: 1 at the very edge, 0 where the bevel ends
  vec2 q = abs(p) - half_ + u_r;
  vec2 sg = vec2(p.x < 0.0 ? -1.0 : 1.0, p.y < 0.0 ? -1.0 : 1.0);
  vec2 n = (q.x > 0.0 && q.y > 0.0) ? normalize(q) : (q.x > q.y ? vec2(1.0, 0.0) : vec2(0.0, 1.0));
  n *= sg;
  float inward = max(-d, 0.0);
  float t = clamp(inward / u_bezel, 0.0, 1.0);    // 0 at the edge, 1 where the bevel meets the flat

  // A flat slab with a narrow rounded bevel, as Apple draws it: the body flat — the plan under it evenly frosted,
  // never magnified — and over the bevel the world just outside bent in, as a quarter-round of glass bends it
  // (none where the bevel meets the flat, most at the very edge), the colours parting a trace at the edge
  vec2 at = u_org + px * u_k;
  float bend = u_refract * u_bezel * pow(1.0 - t, u_curve);
  vec2 off = n * bend * u_k;
  float split = u_disperse * (1.0 - t) * (1.0 - t);
  vec3 col = vec3(behind(at + off * (1.0 + split), u_blur).r, behind(at + off, u_blur).g, behind(at + off * (1.0 - split), u_blur).b);

  // vibrancy (the colour deepened and lifted), a sheet's backdrop dimming it, then the tint: one, all over
  float l = dot(col, vec3(0.2126, 0.7152, 0.0722));
  col = mix(vec3(l), col, u_sat) * u_bright * u_dim;
  col = mix(col, u_tint.rgb, u_tint.a);

  // Light, as Apple draws it: one crisp line of it round the very edge — brightest along the top and the foot,
  // dimmer round the ends — and nothing else. No band, no glow, no shading inside it: the body is flat right
  // up to that line (any light added across the bevel saturates a light glass to a white frame).
  float vy = abs(n.y);
  float upper = smoothstep(-0.25, 0.25, -n.y);     // 1 on the top half, 0 on the foot, smooth round the ends
  // (a CSS pixel wide, crisp: full to half a pixel in, gone a pixel and a quarter in)
  float edge = 1.0 - smoothstep(0.5, 1.25, inward);
  float lineI = mix(u_rimFoot, u_rimTop, upper);
  float rim = edge * mix(u_sideLine, lineI, smoothstep(0.1, 0.9, vy));
  col = mix(col, vec3(1.0), rim);

  // (a breath of grain: no banding in the smooth frost)
  col += (hash(gl_FragCoord.xy) - 0.5) / 255.0;
  o = vec4(clamp(col, 0.0, 1.0) * cover, cover);
}`;

interface Target {
  tex: WebGLTexture;
  fb: WebGLFramebuffer;
  w: number;
  h: number;
}

/** One control's glass: a WebGL canvas, its programs, and its own frost of the plan around it. */
export class GlassCanvas {
  readonly canvas: HTMLCanvasElement;
  #gl: WebGL2RenderingContext;
  #down!: WebGLProgram;
  #up!: WebGLProgram;
  #glass!: WebGLProgram;
  #vao!: WebGLVertexArrayObject;
  #src!: WebGLTexture;
  #levels: Target[] = [];
  #ups: Target[] = [];
  #frost: Target[] = [];
  #stamp = -1;
  #tw = 0;
  #th = 0;
  #uniforms = new Map<WebGLProgram, Map<string, WebGLUniformLocation | null>>();
  lost = false;

  constructor(onLost: () => void) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = 1;
    this.canvas.height = 1;
    const gl = this.canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: true, antialias: false, depth: false, stencil: false, preserveDrawingBuffer: false });
    if (!gl) throw new Error('no WebGL2');
    this.#gl = gl;
    this.canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      this.lost = true;
      onLost();
    });
    this.#make();
  }
  #make(): void {
    const gl = this.#gl;
    const shader = (type: number, src: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, src);
      gl.compileShader(s);
      if (!gl.getShaderParameter(s, gl.COMPILE_STATUS) && !gl.isContextLost()) throw new Error('glass shader: ' + gl.getShaderInfoLog(s));
      return s;
    };
    const program = (frag: string) => {
      const p = gl.createProgram()!;
      gl.attachShader(p, shader(gl.VERTEX_SHADER, VERT));
      gl.attachShader(p, shader(gl.FRAGMENT_SHADER, frag));
      gl.linkProgram(p);
      if (!gl.getProgramParameter(p, gl.LINK_STATUS) && !gl.isContextLost()) throw new Error('glass program: ' + gl.getProgramInfoLog(p));
      return p;
    };
    this.#down = program(DOWN);
    this.#up = program(UP);
    this.#glass = program(GLASS);
    this.#vao = gl.createVertexArray()!;
    this.#src = this.#texture();
  }
  #texture(): WebGLTexture {
    const gl = this.#gl;
    const t = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    return t;
  }
  #target(w: number, h: number): Target {
    const gl = this.#gl;
    const tex = this.#texture();
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    const fb = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    return { tex, fb, w, h };
  }
  #u(p: WebGLProgram, name: string): WebGLUniformLocation | null {
    let m = this.#uniforms.get(p);
    if (!m) this.#uniforms.set(p, (m = new Map()));
    if (!m.has(name)) m.set(name, this.#gl.getUniformLocation(p, name));
    return m.get(name)!;
  }
  /** The pyramid for a region of a size: four levels down (each half the last), and up again to the first from the second, third and fourth. */
  #allocate(w: number, h: number): void {
    const gl = this.#gl;
    for (const t of [...this.#levels, ...this.#ups, ...this.#frost]) {
      gl.deleteTexture(t.tex);
      gl.deleteFramebuffer(t.fb);
    }
    this.#levels = [];
    let lw = w,
      lh = h;
    for (let i = 0; i < 4; i++) {
      lw = Math.max(2, Math.ceil(lw / 2));
      lh = Math.max(2, Math.ceil(lh / 2));
      this.#levels.push(this.#target(lw, lh));
    }
    const [l1, l2, l3] = this.#levels as [Target, Target, Target, Target];
    this.#ups = [this.#target(l3.w, l3.h), this.#target(l2.w, l2.h), this.#target(l2.w, l2.h)];
    // (the breath of frost at the region's own size: one step down and up again)
    this.#frost = [this.#target(w, h), this.#target(l1.w, l1.h), this.#target(l1.w, l1.h), this.#target(l1.w, l1.h)];
  }
  #pass(p: WebGLProgram, src: WebGLTexture, sw: number, sh: number, dst: Target): void {
    const gl = this.#gl;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dst.fb);
    gl.viewport(0, 0, dst.w, dst.h);
    gl.useProgram(p);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, src);
    gl.uniform1i(this.#u(p, 'u_src'), 0);
    gl.uniform2f(this.#u(p, 'u_half'), 0.5 / sw, 0.5 / sh);
    gl.uniform2f(this.#u(p, 'u_out'), dst.w, dst.h);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
  /** The control's region, as copied, taken from the atlas and frosted (only when it is a new copy). */
  #take(cap: Capture, r: Region): void {
    const gl = this.#gl;
    if (r.stamp === this.#stamp) return;
    this.#stamp = r.stamp;
    const w = r.tw,
      h = r.th;
    const resized = w !== this.#tw || h !== this.#th || !this.#frost.length;
    this.#tw = w;
    this.#th = h;
    gl.bindTexture(gl.TEXTURE_2D, this.#src);
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, cap.width);
    gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, r.ax);
    gl.pixelStorei(gl.UNPACK_SKIP_ROWS, r.ay);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, cap.pixels);
    gl.pixelStorei(gl.UNPACK_ROW_LENGTH, 0);
    gl.pixelStorei(gl.UNPACK_SKIP_PIXELS, 0);
    gl.pixelStorei(gl.UNPACK_SKIP_ROWS, 0);
    if (resized) this.#allocate(w, h);
    gl.bindVertexArray(this.#vao);
    gl.disable(gl.BLEND);
    const [l1, l2, l3, l4] = this.#levels as [Target, Target, Target, Target];
    const [u3, u2a, u2b] = this.#ups as [Target, Target, Target];
    const [fine, f0, f1, f2] = this.#frost as [Target, Target, Target, Target];
    this.#pass(this.#down, this.#src, w, h, l1);
    this.#pass(this.#up, l1.tex, l1.w, l1.h, fine);
    this.#pass(this.#down, l1.tex, l1.w, l1.h, l2);
    this.#pass(this.#down, l2.tex, l2.w, l2.h, l3);
    this.#pass(this.#down, l3.tex, l3.w, l3.h, l4);
    // light frost: from the second level; more: from the third, through the second; most: from the fourth
    this.#pass(this.#up, l2.tex, l2.w, l2.h, f0);
    this.#pass(this.#up, l3.tex, l3.w, l3.h, u2a);
    this.#pass(this.#up, u2a.tex, u2a.w, u2a.h, f1);
    this.#pass(this.#up, l4.tex, l4.w, l4.h, u3);
    this.#pass(this.#up, u3.tex, u3.w, u3.h, u2b);
    this.#pass(this.#up, u2b.tex, u2b.w, u2b.h, f2);
  }

  /** The control's glass drawn: its canvas sized to it (device px), the plan round it taken, frosted, refracted and lit. */
  draw(cap: Capture, region: Region, g: PaneGeom, look: GlassLook, view: { w: number; h: number }): void {
    if (this.lost) return;
    const gl = this.#gl;
    const pw = Math.max(1, Math.round(g.w * g.dpr)),
      ph = Math.max(1, Math.round(g.h * g.dpr));
    if (this.canvas.width !== pw || this.canvas.height !== ph) {
      this.canvas.width = pw;
      this.canvas.height = ph;
    }
    this.#take(cap, region);
    const p = this.#glass;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, pw, ph);
    gl.bindVertexArray(this.#vao);
    gl.disable(gl.BLEND);
    gl.useProgram(p);
    this.#frost.forEach((t, i) => {
      gl.activeTexture(gl.TEXTURE0 + i);
      gl.bindTexture(gl.TEXTURE_2D, t.tex);
      gl.uniform1i(this.#u(p, `u_b${i}`), i);
    });
    gl.activeTexture(gl.TEXTURE4);
    gl.bindTexture(gl.TEXTURE_2D, this.#src);
    gl.uniform1i(this.#u(p, 'u_sharp'), 4);
    gl.uniform2f(this.#u(p, 'u_regOrg'), region.x, region.y);
    gl.uniform2f(this.#u(p, 'u_regCss'), region.w, region.h);
    gl.uniform2f(this.#u(p, 'u_view'), view.w, view.h);
    gl.uniform1f(this.#u(p, 'u_ph'), ph);
    gl.uniform1f(this.#u(p, 'u_dpr'), pw / g.w);
    gl.uniform2f(this.#u(p, 'u_size'), g.w, g.h);
    const r = Math.min(g.r, g.w / 2, g.h / 2);
    gl.uniform1f(this.#u(p, 'u_r'), r);
    // the lip: a share of the control's height, at most 26 px, and never wider than its corners are round (a capsule's: most of its round end)
    gl.uniform1f(this.#u(p, 'u_bezel'), Math.max(4, Math.min(10, Math.min(g.w, g.h) * look.bezel)));
    gl.uniform2f(this.#u(p, 'u_org'), g.x, g.y);
    gl.uniform1f(this.#u(p, 'u_k'), g.k);
    gl.uniform4f(this.#u(p, 'u_tint'), ...look.tint);
    gl.uniform1f(this.#u(p, 'u_sat'), look.sat);
    gl.uniform1f(this.#u(p, 'u_bright'), look.bright);
    gl.uniform1f(this.#u(p, 'u_blur'), look.blur);
    gl.uniform1f(this.#u(p, 'u_refract'), look.refract);
    gl.uniform1f(this.#u(p, 'u_curve'), look.curve);
    gl.uniform1f(this.#u(p, 'u_disperse'), look.disperse);
    gl.uniform1f(this.#u(p, 'u_dim'), look.dim);

    gl.uniform1f(this.#u(p, 'u_rimTop'), look.rimTop);
    gl.uniform1f(this.#u(p, 'u_rimFoot'), look.rimFoot);
    gl.uniform1f(this.#u(p, 'u_sideLine'), look.sideLine);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }
  /** Given up: its context let go at once (Safari keeps sixteen at most). */
  dispose(): void {
    this.#gl.getExtension('WEBGL_lose_context')?.loseContext();
    this.lost = true;
  }
}
